import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useUnifiedTranslation } from '../../hooks/useUnifiedTranslation';
import { adminApi } from '../../services/api/admin.api';
import {
  AdminSystemAnalytics,
  AdminSecurityStats,
  AdminBackupStats,
  AdminActionLogsData,
  AdminSecurityIncidentsData,
} from '../../types/admin';
import { DataTable } from '../ui/DataTable';
import { formatShortDate, formatDateTime } from '../../utils/date-utils';
import { PAGE_SIZE } from '../../constants/pagination';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminDashboard: React.FC = () => {
  const { t } = useUnifiedTranslation({ namespace: 'dashboard' });
  const [activeTab, setActiveTab] = useState(0);
  const ts = (key: string, fallback: string): string => {
    const v = t(key);
    return typeof v === 'string' ? v : fallback;
  };

  // Загрузка дашборда (используем getSystemHealth вместо getDashboard)
  const { data: dashboardData, isLoading: _isLoadingDashboard } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => {
      const response = await adminApi.getSystemHealth();
      return { health: response.data };
    },
    refetchInterval: 30000, // Обновлять каждые 30 секунд
  });

  // Загрузка системных метрик
  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ['admin', 'system-metrics'],
    queryFn: async () => {
      const response = await adminApi.getSystemMetrics();
      return response.data;
    },
    // Н-24: было 10 секунд — 6 запросов в минуту при потолке маршрута
    // `GET /admin/system/metrics` в 10/мин (`admin.controller.ts`, @RateLimit(10, 60_000)).
    // До 01.09.2026 потолок был декоративным (@Throttle не доходил до охранника,
    // RL-1), поэтому запас никого не беспокоил. Теперь лимит действует, а вкладки
    // считаются независимо: два открытых кабинета администратора давали бы 12/мин
    // и 429 на живом экране. 30 секунд — тот же интервал, что у соседнего запроса
    // состояния системы, и 2/мин с любым числом вкладок в пределах разумного.
    refetchInterval: 30000, // Обновлять каждые 30 секунд
  });

  // Загрузка пользователей
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const response = await adminApi.getAllUsers(1, 20);
      return response.data;
    },
  });

  // Загрузка логов
  const { data: logsData, isLoading: isLoadingLogs } = useQuery({
    queryKey: ['admin', 'logs'],
    queryFn: async () => {
      const response = await adminApi.getAuditLogs(1, 50);
      return response.data;
    },
    refetchInterval: 15000, // Обновлять каждые 15 секунд
  });

  // Загрузка аналитики (используем getSystemMetrics вместо getSystemAnalytics)
  const { data: analyticsData, isLoading: isLoadingAnalytics } = useQuery<AdminSystemAnalytics>({
    queryKey: ['admin', 'analytics'],
    queryFn: async () => {
      const response = await adminApi.getSystemMetrics();
      return response.data as AdminSystemAnalytics;
    },
  });

  const { data: securityStats, isLoading: isLoadingSecurity } = useQuery<AdminSecurityStats | null>({
    queryKey: ['admin', 'security-stats'],
    queryFn: async () => adminApi.getSecurityStats().catch(() => null),
    enabled: true,
  });

  const { data: backupStats, isLoading: isLoadingBackup } = useQuery<AdminBackupStats | null>({
    queryKey: ['admin', 'backup-stats'],
    queryFn: async () => adminApi.getBackupStats().catch(() => null),
    enabled: true,
  });

  // Загрузка логов действий
  const { data: actionLogsData, isLoading: isLoadingActionLogs } = useQuery<AdminActionLogsData>({
    queryKey: ['admin', 'action-logs'],
    queryFn: async () => {
      const res = await adminApi.getAuditLogs(1, 20);
      const pr = res.data as unknown as { data: AdminActionLogsData['actions']; meta?: { total: number; page: number; limit: number } };
      return {
        actions: pr.data ?? [],
        total: pr.meta?.total ?? (pr.data?.length ?? 0),
        page: pr.meta?.page ?? 1,
        limit: pr.meta?.limit ?? 20,
      };
    },
    refetchInterval: 15000, // Обновлять каждые 15 секунд
  });

  const { data: securityIncidentsData, isLoading: isLoadingSecurityIncidents } = useQuery<AdminSecurityIncidentsData | null>({
    queryKey: ['admin', 'security-incidents'],
    queryFn: async () =>
      adminApi.getSecurityIncidents({ page: 1, limit: PAGE_SIZE.SMALL }).catch(() => ({ incidents: [], total: 0, page: 1, limit: PAGE_SIZE.SMALL })),
    enabled: true,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const metrics = metricsData;

  const userGrowthData = useMemo(
    () => analyticsData?.userGrowth?.map((item) => ({ ...item, monthLabel: item.month })) ?? [],
    [analyticsData?.userGrowth]
  );

  const eventGrowthData = useMemo(
    () => analyticsData?.eventGrowth?.map((item) => ({ ...item, monthLabel: item.month })) ?? [],
    [analyticsData?.eventGrowth]
  );

  // Данные для графика угроз по уровням
  const threatsByLevelData = useMemo(() => {
    if (!securityStats?.incidentsByThreatLevel) return [];
    return Object.entries(securityStats.incidentsByThreatLevel).map(([level, count]) => ({
      level: level.charAt(0).toUpperCase() + level.slice(1),
      count,
    }));
  }, [securityStats?.incidentsByThreatLevel]);

  // Данные для графика топ источников угроз
  const topThreatSourcesData = useMemo(() => {
    if (!securityStats?.topThreatSources) return [];
    return securityStats.topThreatSources.slice(0, 5).map((source) => ({
      ip: source.ipAddress,
      count: source.count,
      level: source.threatLevel,
    }));
  }, [securityStats?.topThreatSources]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {ts('admin.title', 'Панель администратора')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ts('admin.subtitle', 'Системные метрики и управление')}
        </Typography>
      </Box>

      {/* Статус системы */}
      {dashboardData?.health && (
        <Alert
          severity={
            dashboardData.health.status === 'healthy' ? 'success' :
            dashboardData.health.status === 'degraded' ? 'warning' : 'error'
          }
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2">
            Статус системы: {dashboardData.health.status === 'healthy' ? 'Здоров' : 
                            dashboardData.health.status === 'degraded' ? 'Деградирован' : 'Неисправен'}
          </Typography>
        </Alert>
      )}

      {/* Системные метрики */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.totalUsers', 'Всего пользователей')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : metrics?.totalUsers || 0}
                  </Typography>
                  {/*
                    AD-23. Было «Сессий: {metrics?.activeSessions || 0}»: поля
                    `activeSessions` в ответе нет, счётчика живых сессий на бэкенде
                    нет вовсе — подпись всегда читала ноль.
                  */}
                  <Typography variant="caption" color="success.main">
                    Активных за период: {metrics?.activeUsers ?? 0}
                  </Typography>
                </Box>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.totalEvents', 'Всего событий')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : metrics?.totalEvents || 0}
                  </Typography>
                </Box>
                <DashboardIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.memoryUsage', 'Использование памяти')}
                  </Typography>
                  <Typography variant="h5">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : `${metrics?.memoryUsage?.toFixed(1) || 0}%`}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={metrics?.memoryUsage || 0}
                    color={(metrics?.memoryUsage || 0) > 80 ? 'error' : 'primary'}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <MemoryIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.cpuUsage', 'Использование CPU')}
                  </Typography>
                  <Typography variant="h5">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : `${metrics?.cpuUsage?.toFixed(1) || 0}%`}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={metrics?.cpuUsage || 0}
                    color={(metrics?.cpuUsage || 0) > 80 ? 'error' : 'success'}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <SpeedIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.diskUsage', 'Использование диска')}
                  </Typography>
                  <Typography variant="h5">
                    {isLoadingMetrics ? (
                      <CircularProgress size={24} />
                    ) : metrics?.diskUsage == null ? (
                      'н/д'
                    ) : (
                      `${metrics.diskUsage.toFixed(1)}%`
                    )}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={metrics?.diskUsage ?? 0}
                    color={(metrics?.diskUsage ?? 0) > 90 ? 'error' : 'primary'}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <StorageIcon color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.cpuUsage', 'CPU')}
                  </Typography>
                  <Typography variant="h5">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : `${(metrics?.cpuUsage || 0).toFixed(1)}%`}
                  </Typography>
                </Box>
                <SpeedIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/*
          AD-23. Здесь стояла ВТОРАЯ плитка диска: тот же самый `diskUsage`, но с
          подписью «GB». Величина в контракте — процент занятости раздела, поэтому
          «30.0GB» означало 30 % и не значило ничего в гигабайтах. Плитка снята,
          процентная (выше) осталась.
        */}

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  {/*
                    AD-23/AD-24. Было `metrics?.errors24h || 0` — поля нет в ответе,
                    ноль был выдуманным. Контрактное поле `errorCount` nullable:
                    захват 5xx включён (AD-24), чтение счётчика обратно требует
                    `SENTRY_AUTH_TOKEN` (EU-организация + DPA).
                  */}
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.errorCount', 'Ошибок за период')}
                  </Typography>
                  <Typography variant="h5">
                    {isLoadingMetrics ? (
                      <CircularProgress size={24} />
                    ) : metrics?.errorCount == null ? (
                      <Typography variant="body2" color="text.secondary">
                        Источник не подключён
                      </Typography>
                    ) : (
                      metrics.errorCount
                    )}
                  </Typography>
                </Box>
                <SecurityIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Карточка безопасности */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.securityIncidents', 'Инцидентов безопасности')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingSecurity ? <CircularProgress size={24} /> : securityStats?.totalIncidents || 0}
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    Активных: {securityStats?.activeIncidents || 0}
                  </Typography>
                </Box>
                <SecurityIcon color={securityStats && securityStats.activeIncidents > 0 ? 'error' : 'success'} sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Карточка бэкапов */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('admin.kpis.backups', 'Бэкапов')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingBackup ? <CircularProgress size={24} /> : backupStats?.totalBackups || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Следующий: {backupStats?.nextScheduledBackup ? formatShortDate(backupStats.nextScheduledBackup) : '—'}
                  </Typography>
                </Box>
                <StorageIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={ts('admin.tabs.users', 'Пользователи')} icon={<PeopleIcon />} iconPosition="start" />
            <Tab label={ts('admin.tabs.logs', 'Логи')} icon={<DescriptionIcon />} iconPosition="start" />
            <Tab label={ts('admin.tabs.analytics', 'Аналитика')} icon={<AssessmentIcon />} iconPosition="start" />
            <Tab label={ts('admin.tabs.security', 'Безопасность')} icon={<SecurityIcon />} iconPosition="start" />
            <Tab label={ts('admin.tabs.backups', 'Бэкапы')} icon={<StorageIcon />} iconPosition="start" />
            <Tab label={ts('admin.tabs.actions', 'Действия')} icon={<SpeedIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Вкладка: Пользователи */}
        <TabPanel value={activeTab} index={0}>
          {isLoadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : usersData?.users && usersData.users.length > 0 ? (
            <DataTable
              title={ts('admin.usersTitle', 'Управление пользователями')}
              columns={[
                {
                  id: 'email',
                  label: ts('admin.columns.email', 'Email'),
                  sortable: true,
                },
                {
                  id: 'name',
                  label: ts('admin.columns.name', 'Имя'),
                  sortable: true,
                },
                {
                  id: 'role',
                  label: ts('admin.columns.role', 'Роль'),
                  render: (row) => (
                    <Chip label={row.role} size="small" color="primary" />
                  ),
                },
                {
                  id: 'status',
                  label: ts('admin.columns.status', 'Статус'),
                  render: (row) => (
                    <Chip
                      label={row.status}
                      size="small"
                      color={
                        row.status === 'active' ? 'success' :
                        row.status === 'inactive' ? 'default' : 'error'
                      }
                    />
                  ),
                },
                {
                  id: 'department',
                  label: ts('admin.columns.department', 'Отдел'),
                  render: (row: any) => row.department || '—',
                },
                {
                  id: 'lastLogin',
                  label: ts('admin.columns.lastLogin', 'Последний вход'),
                  render: (row: any) => row.lastLogin ? formatShortDate(row.lastLogin) : '—',
                },
              ]}
              data={usersData.users}
              rowKey="id"
              pagination
            />
          ) : (
            <Alert severity="info">
              {ts('admin.noUsers', 'Нет пользователей')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Логи */}
        <TabPanel value={activeTab} index={1}>
          {isLoadingLogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : logsData?.data && logsData.data.length > 0 ? (
            <DataTable
              title={ts('admin.logsTitle', 'Системные логи')}
              columns={[
                {
                  id: 'createdAt',
                  label: ts('admin.columns.timestamp', 'Время'),
                  sortable: true,
                  render: (row: any) => formatDateTime(row.createdAt),
                },
                {
                  id: 'status',
                  label: ts('admin.columns.status', 'Статус'),
                  render: (row) => (
                    <Chip
                      label={row.status}
                      size="small"
                      color={row.status === 'success' ? 'success' : 'error'}
                    />
                  ),
                },
                {
                  id: 'action',
                  label: ts('admin.columns.action', 'Действие'),
                },
                {
                  id: 'resource',
                  label: ts('admin.columns.resource', 'Ресурс'),
                },
                {
                  id: 'userId',
                  label: ts('admin.columns.user', 'Пользователь'),
                  render: (row: any) => row.userId || '—',
                },
                {
                  id: 'ipAddress',
                  label: ts('admin.columns.ip', 'IP'),
                  render: (row: any) => row.ipAddress || '—',
                },
              ]}
              data={logsData.data}
              rowKey="id"
              pagination
            />
          ) : (
            <Alert severity="info">
              {ts('admin.noLogs', 'Нет логов')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Аналитика */}
        <TabPanel value={activeTab} index={2}>
          {isLoadingAnalytics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : analyticsData ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.analytics.userGrowth', 'Рост пользователей')}
                    </Typography>
                    {userGrowthData.length ? (
                      <Box sx={{ height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={userGrowthData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthLabel" />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip formatter={(value: number) => value.toString()} />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="count"
                              name={ts('admin.analytics.userCount', 'Пользователи')}
                              stroke="#3f51b5"
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {ts('admin.analytics.noUserGrowth', 'Нет данных для построения графика')}
                      </Alert>
                    )}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Последний месяц: +{analyticsData.userGrowth?.[analyticsData.userGrowth.length - 1]?.count || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.analytics.eventGrowth', 'Рост событий')}
                    </Typography>
                    {eventGrowthData.length ? (
                      <Box sx={{ height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={eventGrowthData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthLabel" />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip formatter={(value: number) => value.toString()} />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="count"
                              name={ts('admin.analytics.eventCount', 'События')}
                              stroke="#009688"
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {ts('admin.analytics.noEventGrowth', 'Нет данных для построения графика')}
                      </Alert>
                    )}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Последний месяц: +{analyticsData.eventGrowth?.[analyticsData.eventGrowth.length - 1]?.count || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.analytics.topCategories', 'Популярные категории')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {analyticsData.topCategories?.map((cat, index) => (
                        <Chip
                          key={index}
                          label={`${cat.category} (${cat.count})`}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">
              {ts('admin.noAnalytics', 'Нет данных аналитики')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Безопасность */}
        <TabPanel value={activeTab} index={3}>
          {isLoadingSecurity ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : securityStats ? (
            <Grid container spacing={3}>
              {/* График угроз по уровням */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.security.incidentsByLevel', 'Инциденты по уровням угроз')}
                    </Typography>
                    {threatsByLevelData.length > 0 ? (
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={threatsByLevelData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="level" />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip />
                            <Legend />
                            <Bar dataKey="count" fill="#3f51b5" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {ts('admin.security.noThreats', 'Нет данных об угрозах')}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Топ источников угроз */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.security.topThreatSources', 'Топ источников угроз')}
                    </Typography>
                    {topThreatSourcesData.length > 0 ? (
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={topThreatSourcesData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="ip" type="category" width={120} />
                            <RechartsTooltip />
                            <Legend />
                            <Bar dataKey="count" fill="#f44336" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {ts('admin.security.noThreatSources', 'Нет данных об источниках угроз')}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Последние инциденты */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.security.recentIncidents', 'Последние инциденты')}
                    </Typography>
                    {isLoadingSecurityIncidents ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress />
                      </Box>
                    ) : securityIncidentsData?.incidents && securityIncidentsData.incidents.length > 0 ? (
                      <DataTable
                        columns={[
                          {
                            id: 'incidentType',
                            label: ts('admin.security.columns.type', 'Тип'),
                          },
                          {
                            id: 'threatLevel',
                            label: ts('admin.security.columns.threatLevel', 'Уровень угрозы'),
                            render: (row) => (
                              <Chip
                                label={row.threatLevel}
                                size="small"
                                color={
                                  row.threatLevel === 'critical' ? 'error' :
                                  row.threatLevel === 'high' ? 'error' :
                                  row.threatLevel === 'medium' ? 'warning' : 'success'
                                }
                              />
                            ),
                          },
                          {
                            id: 'ipAddress',
                            label: ts('admin.security.columns.ip', 'IP адрес'),
                          },
                          {
                            id: 'status',
                            label: ts('admin.security.columns.status', 'Статус'),
                            render: (row) => (
                              <Chip
                                label={row.status}
                                size="small"
                                color={
                                  row.status === 'active' ? 'error' :
                                  row.status === 'resolved' ? 'success' :
                                  row.status === 'investigating' ? 'warning' : 'default'
                                }
                              />
                            ),
                          },
                          {
                            id: 'createdAt',
                            label: ts('admin.security.columns.date', 'Дата'),
                            render: (row) => formatDateTime(row.createdAt),
                          },
                        ]}
                        data={securityIncidentsData.incidents}
                        rowKey="id"
                        pagination
                      />
                    ) : (
                      <Alert severity="info">
                        {ts('admin.security.noIncidents', 'Нет инцидентов')}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">
              {ts('admin.security.noData', 'Нет данных безопасности')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Бэкапы */}
        <TabPanel value={activeTab} index={4}>
          {isLoadingBackup ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : backupStats ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.backup.totalBackups', 'Всего бэкапов')}
                    </Typography>
                    <Typography variant="h4">{backupStats.totalBackups}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Успешных: {backupStats.completedBackups}
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      Неудачных: {backupStats.failedBackups}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.backup.storage', 'Хранилище')}
                    </Typography>
                    <Typography variant="h4">
                      {((backupStats.storageUsed / 1024 / 1024 / 1024).toFixed(2))} GB
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Использовано: {backupStats.storageUsagePercentage.toFixed(1)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={backupStats.storageUsagePercentage}
                      color={backupStats.storageUsagePercentage > 80 ? 'error' : 'primary'}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.backup.schedule', 'Расписание')}
                    </Typography>
                    <Typography variant="body1">
                      {ts('admin.backup.nextBackup', 'Следующий бэкап:')}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {backupStats.nextScheduledBackup
                        ? formatDateTime(backupStats.nextScheduledBackup)
                        : '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Частота: {backupStats.backupFrequency}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('admin.backup.statistics', 'Статистика бэкапов')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          {ts('admin.backup.totalSize', 'Общий размер')}
                        </Typography>
                        <Typography variant="h6">
                          {((backupStats.totalSize / 1024 / 1024 / 1024).toFixed(2))} GB
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          {ts('admin.backup.averageSize', 'Средний размер')}
                        </Typography>
                        <Typography variant="h6">
                          {((backupStats.averageSize / 1024 / 1024).toFixed(2))} MB
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          {ts('admin.backup.lastBackup', 'Последний бэкап')}
                        </Typography>
                        <Typography variant="h6">
                          {backupStats.lastBackupDate
                            ? formatShortDate(backupStats.lastBackupDate)
                            : '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          {ts('admin.backup.oldestBackup', 'Самый старый')}
                        </Typography>
                        <Typography variant="h6">
                          {backupStats.oldestBackup
                            ? formatShortDate(backupStats.oldestBackup)
                            : '—'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">
              {ts('admin.backup.noData', 'Нет данных о бэкапах')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Действия */}
        <TabPanel value={activeTab} index={5}>
          {isLoadingActionLogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : actionLogsData?.actions && actionLogsData.actions.length > 0 ? (
            <DataTable
              title={ts('admin.actions.title', 'Логи действий')}
              columns={[
                {
                  id: 'action',
                  label: ts('admin.actions.columns.action', 'Действие'),
                  sortable: true,
                },
                {
                  id: 'description',
                  label: ts('admin.actions.columns.description', 'Описание'),
                },
                {
                  id: 'status',
                  label: ts('admin.actions.columns.status', 'Статус'),
                  render: (row) => (
                    <Chip
                      label={row.status}
                      size="small"
                      color={
                        row.status === 'completed' ? 'success' :
                        row.status === 'failed' ? 'error' :
                        row.status === 'in_progress' ? 'warning' : 'default'
                      }
                    />
                  ),
                },
                {
                  id: 'executedBy',
                  label: ts('admin.actions.columns.executedBy', 'Выполнил'),
                  render: (row) => row.executedBy?.name || '—',
                },
                {
                  id: 'createdAt',
                  label: ts('admin.actions.columns.createdAt', 'Создано'),
                  sortable: true,
                  render: (row) => formatDateTime(row.createdAt),
                },
                {
                  id: 'executedAt',
                  label: ts('admin.actions.columns.executedAt', 'Выполнено'),
                  render: (row) => row.executedAt ? formatDateTime(row.executedAt) : '—',
                },
              ]}
              data={actionLogsData.actions}
              rowKey="id"
              pagination
            />
          ) : (
            <Alert severity="info">
              {ts('admin.actions.noActions', 'Нет действий')}
            </Alert>
          )}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default AdminDashboard;

